import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Filter,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Ban,
  Eye
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const ReportsManagement = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [page, statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter && { status: statusFilter })
      });
      
      const response = await api.get(`/admin/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReports(response.data.reports);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId, action) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(
        `/admin/reports/${reportId}/resolve?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Report resolved: ${action}`);
      setSelectedReport(null);
      fetchReports();
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      resolved: 'bg-green-500/20 text-green-400'
    };
    return styles[status] || styles.pending;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Reports & Moderation</h1>
        <span className="text-gray-400">{total} total reports</span>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <Filter className="text-gray-400 w-5 h-5" />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="bg-gray-800 border border-gray-700 rounded-lg text-white px-4 py-2"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {/* Reports Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr className="text-left text-gray-400 text-sm">
                <th className="px-6 py-4">Reporter</th>
                <th className="px-6 py-4">Reported User</th>
                <th className="px-6 py-4">Reason</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                reports.map((report) => (
                  <tr key={report.id} className="border-t border-gray-700 hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <p className="text-white">{report.reporter?.name || 'Unknown'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white">{report.reported_user?.name || 'Unknown'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-300">{report.reason}</p>
                      {report.details && (
                        <p className="text-gray-500 text-sm truncate max-w-xs">{report.details}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(report.status)}`}>
                        {report.status || 'pending'}
                      </span>
                      {report.resolution && (
                        <p className="text-xs text-gray-500 mt-1">{report.resolution}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(report.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {report.status !== 'resolved' ? (
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                        >
                          Review
                        </button>
                      ) : (
                        <span className="text-gray-500 text-sm">Resolved</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <p className="text-gray-400 text-sm">
            Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white px-4">{page} / {totalPages || 1}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Review Report</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-gray-400 text-sm">Reporter</p>
                <p className="text-white">{selectedReport.reporter?.name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Reported User</p>
                <p className="text-white">{selectedReport.reported_user?.name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Reason</p>
                <p className="text-white">{selectedReport.reason}</p>
              </div>
              {selectedReport.details && (
                <div>
                  <p className="text-gray-400 text-sm">Details</p>
                  <p className="text-gray-300">{selectedReport.details}</p>
                </div>
              )}
              
              <div className="pt-4 border-t border-gray-700">
                <p className="text-white font-medium mb-3">Take Action</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleResolve(selectedReport.id, 'dismiss')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                  >
                    <XCircle className="w-5 h-5" />
                    <span>Dismiss</span>
                  </button>
                  <button
                    onClick={() => handleResolve(selectedReport.id, 'warn')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-600/20 text-yellow-400 rounded-lg hover:bg-yellow-600/30"
                  >
                    <AlertTriangle className="w-5 h-5" />
                    <span>Warn User</span>
                  </button>
                  <button
                    onClick={() => handleResolve(selectedReport.id, 'suspend')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-orange-600/20 text-orange-400 rounded-lg hover:bg-orange-600/30"
                  >
                    <Ban className="w-5 h-5" />
                    <span>Suspend</span>
                  </button>
                  <button
                    onClick={() => handleResolve(selectedReport.id, 'ban')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30"
                  >
                    <Ban className="w-5 h-5" />
                    <span>Ban User</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={() => setSelectedReport(null)}
                className="w-full py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsManagement;
